'use client';

import { useState, useMemo, useEffect } from 'react';

export type EventType = 'Вебинар' | 'Митап' | 'Воркшоп' | 'Конференция';
export type EventFormat = 'online' | 'offline';

export interface EventItem {
  id: string;
  title: string;
  type: EventType;
  format: EventFormat;
  city: string;
  tz: string;
  start: string;
  end: string;
  views: number;
  cover: string;
  desc?: string;
  tags?: string[];
  price: number;
  regUrl?: string;
  venue?: string;
}

const EVENTS: EventItem[] = [
  {
    id: 'e1',
    title: 'Вебинар: RPO за 14 дней',
    type: 'Вебинар',
    format: 'online',
    city: 'Онлайн',
    tz: 'Europe/Moscow',
    start: '2025-08-28T12:00:00',
    end: '2025-08-28T13:00:00',
    views: 820,
    cover: 'https://placehold.co/800x480?text=Webinar+RPO',
    desc: 'Пошаговый план быстрого запуска RPO: KPI, SLA, воронка, BI.',
    tags: ['RPO', 'KPI', 'SLA'],
    price: 0,
    regUrl: '#',
  },
  {
    id: 'e2',
    title: 'INTERIM Meetup: массовый подбор в ритейле',
    type: 'Митап',
    format: 'offline',
    city: 'Грозный',
    tz: 'Europe/Moscow',
    start: '2025-09-10T18:00:00',
    end: '2025-09-10T20:30:00',
    views: 640,
    cover: 'https://placehold.co/800x480?text=Meetup+Retail',
    desc: 'Каналы, конверсии и явка: кейсы маркетплейсов и ритейла.',
    tags: ['Ритейл', 'Маркетплейс'],
    price: 0,
    venue: 'INTERIM Hub, Савеловская',
  },
  {
    id: 'e3',
    title: 'Воркшоп: ATS & BI для рекрутмента',
    type: 'Воркшоп',
    format: 'offline',
    city: 'Гудермес',
    tz: 'Europe/Moscow',
    start: '2025-09-03T10:00:00',
    end: '2025-09-03T13:00:00',
    views: 510,
    cover: 'https://placehold.co/800x480?text=Workshop+ATS+BI',
    desc: 'Практика: воронка, отчёты, слоты интервью, no-show.',
    tags: ['ATS', 'BI'],
    price: 2500,
    venue: 'Коворкинг Лиговский',
  },
  {
    id: 'e4',
    title: 'Live-подкаст: KPI & SLA',
    type: 'Вебинар',
    format: 'online',
    city: 'Онлайн',
    tz: 'Europe/Moscow',
    start: '2025-08-22T16:00:00',
    end: '2025-08-22T17:00:00',
    views: 740,
    cover: 'https://placehold.co/800x480?text=Live+Podcast',
    desc: 'Разбираем KPI, SLA и скорость найма с гостями.',
    tags: ['Подкаст', 'KPI'],
    price: 0,
    regUrl: '#',
  },
  {
    id: 'e5',
    title: 'E-commerce HR Summit',
    type: 'Конференция',
    format: 'offline',
    city: 'Грозный',
    tz: 'Asia/Almaty',
    start: '2025-10-07T09:30:00',
    end: '2025-10-07T18:00:00',
    views: 1210,
    cover: 'https://placehold.co/800x480?text=HR+Summit',
    desc: 'Большая конференция: e-commerce, логистика, клиентский сервис.',
    tags: ['E-commerce', 'Логистика'],
    price: 9000,
    venue: 'Expo Центр',
  },
  {
    id: 'e6',
    title: 'Весенний карьерный день',
    type: 'Митап',
    format: 'offline',
    city: 'Урус-Мартан',
    tz: 'Asia/Novosibirsk',
    start: '2025-05-20T12:00:00',
    end: '2025-05-20T16:00:00',
    views: 300,
    cover: 'https://placehold.co/800x480?text=Career+Day',
    desc: 'Практикум резюме и speed-интервью. Прошедшее событие.',
    tags: ['Карьерный день'],
    price: 0,
    venue: 'Технопарк',
  },
];

const ruShort = (s: string) => new Date(s).toLocaleString('ru-RU', { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
const ruDate = (s: string) => new Date(s).toLocaleString('ru-RU', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
const money = (n: number) => (n === 0 || n === null) ? 'Бесплатно' : (n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' ₽');
const now = new Date();

const gcalLink = (ev: EventItem) => {
  const toIsoZ = (s: string) => new Date(s).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');
  const dates = `${toIsoZ(ev.start)}/${toIsoZ(ev.end)}`;
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(ev.title)}&details=${encodeURIComponent(ev.desc || '')}&location=${encodeURIComponent(ev.venue || ev.city || '')}&dates=${dates}&ctz=${encodeURIComponent(ev.tz || 'UTC')}`;
};

const downloadICS = (ev: EventItem) => {
  const pad = (n: number) => String(n).padStart(2, '0');
  const dtICS = (s: string) => {
    const d = new Date(s);
    return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}T${pad(d.getHours())}${pad(d.getMinutes())}00`;
  };
  const escICS = (s: string) => (s || '').replace(/,/g, '\\,').replace(/;/g, '\\;').replace(/\n/g, '\\n');
  const ics = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//INTERIM//Events//RU
CALSCALE:GREGORIAN
METHOD:PUBLISH
X-WR-CALNAME:INTERIM Events
X-WR-TIMEZONE:${ev.tz || 'UTC'}
BEGIN:VEVENT
UID:${ev.id}@interim.local
DTSTAMP:${dtICS(new Date().toISOString())}Z
DTSTART;TZID=${ev.tz || 'UTC'}:${dtICS(ev.start)}
DTEND;TZID=${ev.tz || 'UTC'}:${dtICS(ev.end)}
SUMMARY:${escICS(ev.title)}
DESCRIPTION:${escICS(ev.desc || '')}
LOCATION:${escICS(ev.venue || ev.city || '')}
END:VEVENT
END:VCALENDAR`;
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = (ev.title || 'event').replace(/\s+/g, '_') + '.ics';
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
};

interface EventCardProps {
  item: EventItem;
  onOpenModal: (item: EventItem) => void;
}

const EventCard = ({ item, onOpenModal }: EventCardProps) => {
  const past = new Date(item.end) < now;
  const isFree = item.price === 0;
  const isPopular = (item.views || 0) > 1000;

  const statusBadges = () => {
    const badges = [];
    if (isFree) badges.push(<span key="free" className="badge badge-free">Free</span>);
    if (past) badges.push(<span key="past" className="badge badge-past">Завершилось</span>);
    if (isPopular) badges.push(<span key="hot" className="badge badge-hot">Популярно</span>);
    return badges;
  };

  return (
    <article className="card rounded-2xl overflow-hidden flex flex-col">
      <img
        src={item.cover}
        alt=""
        className="w-full h-44 object-cover"
        onError={(e) => (e.currentTarget.src = 'https://placehold.co/800x480?text=Event')}
      />
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-xl font-semibold">{item.title}</h3>
            <div className="mt-1 text-xs flex flex-wrap gap-2">
              <span className="tag">{item.type}</span>
              <span className="tag">{item.format === 'online' ? 'Онлайн' : (item.city || '—')}</span>
              <span className="tag">{ruShort(item.start)} — {ruShort(item.end)}</span>
            </div>
          </div>
          <div className="text-xs flex flex-col items-end gap-1">{statusBadges()}</div>
        </div>

        <p className="text-sm text-gray-200 mt-3 line-clamp-3">{item.desc || ''}</p>
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
          {(item.tags || []).slice(0, 3).map((t) => (
            <span key={t} className="tag">{t}</span>
          ))}
          <span className="tag">Посещения: {item.views || 0}</span>
          <span className="tag">Стоимость: {money(item.price)}</span>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div className="flex gap-2 flex-wrap">
            <a className="btn btn-ghost text-sm" href={gcalLink(item)} target="_blank" rel="noopener">
              Google Calendar
            </a>
            <button className="btn btn-ghost text-sm" onClick={() => downloadICS(item)}>
              .ics
            </button>
          </div>
          <button className="btn btn-accent text-sm" onClick={() => onOpenModal(item)}>
            Подробнее
          </button>
        </div>
      </div>
    </article>
  );
};

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventItem: EventItem | null;
  onDownloadICS: (item: EventItem) => void;
  onAddGCal: (item: EventItem) => string;
}

const Modal = ({ isOpen, onClose, eventItem, onDownloadICS, onAddGCal }: ModalProps) => {
  if (!isOpen || !eventItem) return null;
  return (
    <div className={`modal ${isOpen ? 'open' : ''}`} aria-hidden={!isOpen} role="dialog" aria-label="Подробности события" onClick={onClose}>
      <div className="modal-content card rounded-2xl p-6 max-w-3xl w-[92%] relative" onClick={(e) => e.stopPropagation()}>
        <button id="close" className="absolute right-3 top-3 px-2 py-1 rounded-lg btn-ghost" aria-label="Закрыть" onClick={onClose}> ✕ </button>
        <div id="mc">
          <div className="grid gap-4 md:grid-cols-2">
            <img src={eventItem.cover} alt="" className="rounded-xl w-full h-56 object-cover" />
            <div>
              <h3 className="text-2xl font-bold">{eventItem.title}</h3>
              <div className="mt-2 text-xs flex flex-wrap gap-2">
                <span className="tag">{eventItem.type}</span>
                <span className="tag">{eventItem.format === 'online' ? 'Онлайн' : (eventItem.city || '')}</span>
                <span className="tag">{ruDate(eventItem.start)} — {ruDate(eventItem.end)}</span>
                <span className="tag">Часовой пояс: {eventItem.tz || '—'}</span>
              </div>
              <p className="text-sm text-gray-200 mt-3">{eventItem.desc || ''}</p>
              <div className="mt-2 text-sm">
                <b>Стоимость:</b> {money(eventItem.price)} {eventItem.venue ? ` • <b>Место:</b> ${eventItem.venue}` : ''}
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <a className="btn btn-ghost" href={onAddGCal(eventItem)} target="_blank" rel="noopener">Добавить в Google Calendar</a>
                <button className="btn btn-ghost" onClick={() => onDownloadICS(eventItem)}>Скачать .ics</button>
                <button className="btn btn-accent">Зарегистрироваться</button>
              </div>
            </div>
          </div>
          <form id="reg-form" className="mt-6 card rounded-2xl p-6 grid gap-4 md:grid-cols-2" onSubmit={(e) => { e.preventDefault(); alert('Спасибо! Регистрация отправлена.'); }}>
            <input className="rounded-xl px-4 py-3 text-gray-900" placeholder="Имя и фамилия*" required />
            <input className="rounded-xl px-4 py-3 text-gray-900" placeholder="E-mail*" type="email" required />
            <input className="rounded-xl px-4 py-3 text-gray-900" placeholder="Телефон*" pattern="^[0-9+()\-\s]{6,}$" required />
            <select className="rounded-xl px-4 py-3 text-gray-900" required>
              <option value="">Формат участия*</option>
              <option>Онлайн</option>
              <option>Офлайн</option>
            </select>
            <textarea className="rounded-xl px-4 py-3 text-gray-900 md:col-span-2" rows={4} placeholder="Комментарий (необязательно)"></textarea>
            <div className="text-xs text-gray-300 md:col-span-2">Событие: <b>{eventItem.title}</b> ({ruDate(eventItem.start)})</div>
            <button className="btn btn-accent md:col-span-2">Отправить регистрации</button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default function EventsPage() {
  const [query, setQuery] = useState('');
  const [selectedType, setSelectedType] = useState<EventType | ''>('');
  const [selectedFormat, setSelectedFormat] = useState<EventFormat | ''>('');
  const [selectedCity, setSelectedCity] = useState('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [showUpcoming, setShowUpcoming] = useState(true);
  const [sortBy, setSortBy] = useState('dateAsc');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<EventItem | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 6;

  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    const plus90 = new Date();
    plus90.setDate(new Date().getDate() + 90);
    setStartDate(today);
    setEndDate(plus90.toISOString().slice(0, 10));
  }, []);

  const filteredAndSortedEvents = useMemo(() => {
    let items = EVENTS.filter((e) => {
      const hay = (e.title + ' ' + (e.desc || '') + ' ' + (e.tags || []).join(' ') + ' ' + (e.venue || '') + ' ' + (e.city || '')).toLowerCase();
      const eventDate = new Date(e.start);
      const isUpcoming = new Date(e.end) >= now;
      return (
        (!query || hay.includes(query.toLowerCase())) &&
        (!selectedType || e.type === selectedType) &&
        (!selectedFormat || e.format === selectedFormat) &&
        (!selectedCity || e.city === selectedCity) &&
        (!startDate || eventDate >= new Date(startDate)) &&
        (!endDate || eventDate <= new Date(endDate)) &&
        (!showUpcoming || isUpcoming)
      );
    });

    if (sortBy === 'dateAsc') {
      items.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
    } else if (sortBy === 'dateDesc') {
      items.sort((a, b) => new Date(b.start).getTime() - new Date(a.start).getTime());
    } else if (sortBy === 'title') {
      items.sort((a, b) => a.title.localeCompare(b.title, 'ru'));
    } else if (sortBy === 'pop') {
      items.sort((a, b) => (b.views || 0) - (a.views || 0));
    }

    return items;
  }, [query, selectedType, selectedFormat, selectedCity, startDate, endDate, showUpcoming, sortBy]);

  useEffect(() => {
    setCurrentPage(1);
  }, [query, selectedType, selectedFormat, selectedCity, startDate, endDate, showUpcoming, sortBy]);

  const paginatedEvents = useMemo(() => {
    const start = (currentPage - 1) * perPage;
    return filteredAndSortedEvents.slice(start, start + perPage);
  }, [filteredAndSortedEvents, currentPage]);

  const totalPages = Math.ceil(filteredAndSortedEvents.length / perPage);

  const totalStats = useMemo(() => ({
    all: EVENTS.length,
    upcoming: EVENTS.filter(e => new Date(e.end) >= now).length,
    past: EVENTS.filter(e => new Date(e.end) < now).length,
  }), []);

  const handleOpenModal = (item: EventItem) => {
    setSelectedEvent(item);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedEvent(null);
  };

  return (
    <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-gray-100">
      <style jsx global>{`
        .modal {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
          overflow-y: auto;
          padding: 20px;
        }
        .modal-content {
          margin-top: auto;
          margin-bottom: auto;
          animation: modal-fadein 0.3s ease-out;
        }
        @keyframes modal-fadein {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>

      <header className="px-6 pt-32 pb-16 md:pt-40 md:pb-20"
        style={{ background: 'linear-gradient(rgba(0,0,0,.45), rgba(0,0,0,.25)), radial-gradient(1200px 600px at 70% -10%, rgba(255,255,255,.08), transparent)' }}>
        <div className="max-w-7xl mx-auto">
          <nav className="mb-6 flex flex-wrap gap-3 text-sm">
            <a href="#list" className="chip px-3 py-1 rounded-full hover:bg-white/25">Список событий</a>
            <a href="#filters" className="chip px-3 py-1 rounded-full hover:bg-white/25">Фильтры</a>
            <a href="#faq" className="chip px-3 py-1 rounded-full hover:bg-white/25">FAQ</a>
          </nav>
          <h1 className="text-4xl md:text-6xl font-extrabold mb-4">События INTERIM</h1>
          <p className="max-w-3xl text-lg md:text-xl text-gray-200">
            Вебинары, митапы, воркшопы и конференции о массовом подборе, RPO, ATS/BI и клиентском сервисе.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a href="#list" className="btn btn-ghost">Смотреть события</a>
            <a href="#filters" className="btn btn-accent">Настроить фильтры</a>
          </div>
        </div>
      </header>

      <main className="px-6 py-12 md:py-16 max-w-7xl mx-auto space-y-16">
        <section id="filters" aria-label="Фильтры событий" className="scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Подбор по параметрам</h2>

          <div className="grid gap-3 md:grid-cols-6">
            <input
              id="q"
              className="rounded-xl px-3 py-2 text-gray-900 h-10 md:col-span-2"
              placeholder="Поиск по названию/описанию…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <select
              id="etype"
              className="rounded-xl px-3 py-2 text-gray-900 h-10"
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value as EventType | '')}
            >
              <option value="">Тип: все</option>
              <option>Вебинар</option>
              <option>Митап</option>
              <option>Воркшоп</option>
              <option>Конференция</option>
            </select>
            <select
              id="format"
              className="rounded-xl px-3 py-2 text-gray-900 h-10"
              value={selectedFormat}
              onChange={(e) => setSelectedFormat(e.target.value as EventFormat | '')}
            >
              <option value="">Формат: все</option>
              <option value="online">Онлайн</option>
              <option value="offline">Офлайн</option>
            </select>
            <select
              id="city"
              className="rounded-xl px-3 py-2 text-gray-900 h-10"
              value={selectedCity}
              onChange={(e) => setSelectedCity(e.target.value)}
            >
              <option value="">Город: все</option>
              <option>Грозный</option>
              <option>Гудермес</option>
              <option>Урус-Мартан</option>
              <option>Аргун</option>
              <option>Онлайн</option>
            </select>
            <input
              type="date"
              className="rounded-xl px-3 py-2 text-gray-900 h-10"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            <input
              type="date"
              className="rounded-xl px-3 py-2 text-gray-900 h-10"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>

          <div className="mt-3 grid gap-3 md:grid-cols-3">
            <label
              className="card rounded-xl px-4 py-2 flex items-center gap-3 cursor-pointer transition-none hover:scale-100 hover:shadow-none hover:bg-inherit"
            >
              <input
                id="upcoming"
                type="checkbox"
                className="w-5 h-5"
                checked={showUpcoming}
                onChange={(e) => setShowUpcoming(e.target.checked)}
              />
              <span className="text-sm text-gray-200">Показывать только предстоящие</span>
            </label>

            <select
              id="sort"
              className="rounded-xl px-3 py-2 text-gray-900 h-10"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="dateAsc">Сортировать: ближайшие</option>
              <option value="dateDesc">по дате (позже)</option>
              <option value="title">по названию</option>
              <option value="pop">по популярности</option>
            </select>

            <div
              className="card rounded-xl px-4 py-2 text-sm text-gray-200"
              id="stats"
              style={{ transform: 'none', transition: 'none' }}
            >
              Всего: <b>{totalStats.all}</b> • Предстоящие: <b>{totalStats.upcoming}</b> • Прошедшие: <b>{totalStats.past}</b>
            </div>

          </div>
        </section>


        <section id="list" aria-label="Список событий" className="scroll-mt-32">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <h2 className="text-3xl md:text-4xl font-bold">События</h2>
            <span id="count" className="text-sm text-gray-300">Найдено событий: {filteredAndSortedEvents.length}</span>
          </div>
          <div id="grid" className="mt-6 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {paginatedEvents.length > 0 ? (
              paginatedEvents.map(item => (
                <EventCard key={item.id} item={item} onOpenModal={handleOpenModal} />
              ))
            ) : (
              <p id="empty" className="hidden text-gray-300">Ничего не найдено — измените фильтры.</p>
            )}
          </div>
          <div id="pager" className="mt-6 flex flex-wrap items-center gap-2">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                className={`px-3 py-2 rounded-lg ${page === currentPage ? 'btn-accent' : 'btn-ghost'}`}
                onClick={() => setCurrentPage(page)}>
                {page}
              </button>
            ))}
          </div>
        </section>

        <section id="faq" className="scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">FAQ</h2>
          <div className="space-y-3">
            <details className="simple-card">
              <summary className="cursor-pointer font-semibold">Как добавить событие в календарь?</summary>
              <p className="mt-2 text-sm text-gray-200">В карточке нажмите «Google Calendar» или «.ics» для локального календаря.</p>
            </details>
            <details className="simple-card">
              <summary className="cursor-pointer font-semibold">Онлайн-ссылку пришлёте?</summary>
              <p className="mt-2 text-sm text-gray-200">Да, после регистрации мы отправим ссылку на e-mail.</p>
            </details>
          </div>
        </section>
      </main>
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        eventItem={selectedEvent}
        onDownloadICS={downloadICS}
        onAddGCal={gcalLink}
      />
    </div>
  );
}